// app/javascript/controllers/chat_controller.js
import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'chatContainer',
    'availabilityContainer',
    'input',
    'history',
    'availabilityHistory',
    'createServiceEventButton',
    'serviceProviderSelect',
    'bookableResourceSelect',
    'pickADate',
    'startTime',
  ];

  connect() {
    console.log('Chat controller connected');
    this.threadId = null;
    this.serviceDetails = null;
    this.pickADateTarget.addEventListener('change', this.chooseDate.bind(this));
  }

  setThreadId(id) {
    this.threadId = id; // Method to set thread ID after creation
  }

  setServiceDetails(details) {
    this.serviceDetails = details; // Method to set service details
  }

  toggleChat(event) {
    const serviceId = event.target.value;
    if (serviceId) {
      this.chatContainerTarget.style.display = 'block';
      this.availabilityContainerTarget.style.display = 'block';
      this.createThread(serviceId);
    } else {
      this.chatContainerTarget.style.display = 'none';
      this.availabilityContainerTarget.style.display = 'none';
    }
  }

  createThread(serviceId) {
    fetch(`/service_events/create_thread`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ service_id: serviceId }),
    })
      .then(response => response.json())
      .then(data => {
        if (data.thread_id) {
          console.log('Thread created with ID:', data.thread_id);
          this.setThreadId(data.thread_id);
          this.fetchServiceDetails(serviceId); // Fetch service details after creating thread
        } else {
          console.error('Failed to retrieve thread ID:', data);
        }
      })
      .catch(error => console.error('Error creating thread:', error));
  }

  fetchServiceDetails(serviceId) {
    fetch(`/service_details/${serviceId}`)
      .then(response => response.json())
      .then(data => {
        this.setServiceDetails(data.message); // Set the service details
        const instructionsTextarea = document.getElementById(
          'instructionsTextarea'
        );
        instructionsTextarea.value = this.prependCurrentDate(data.message); // Populate the modal with service details and current date
      })
      .catch(error => console.error('Error fetching service details:', error));
  }

  prependCurrentDate(message) {
    const now = new Date();
    const currentDate = now.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const currentTime = now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return `**Today is ${currentDate} and the time is ${currentTime}.**\n\n${message}`;
  }

  send(event) {
    event.preventDefault(); // Prevent the form from submitting
    const message = this.inputTarget.value;
    this.inputTarget.value = ''; // Clear input after sending
    const userMessageElement = document.createElement('div');
    userMessageElement.classList.add('chatgpt-message', 'user');
    userMessageElement.textContent = `You: ${message}`;
    this.historyTarget.appendChild(userMessageElement);

    if (message.trim() !== '' && this.threadId) {
      this.scrollToBottom();

      // Show loading indicator
      this.showLoadingIndicator();
      const instructionsTextarea = document.getElementById(
        'instructionsTextarea'
      );
      const editedInstructions = this.prependCurrentDate(
        instructionsTextarea.value
      );

      if (['development', 'staging'].includes(process.env.RAILS_ENV)) {
        $('#editInstructionsModal').modal('show');

        document.getElementById('saveEditedInstructions').onclick = () => {
          this.createRunWithInstructions(message, editedInstructions);
          $('#editInstructionsModal').modal('hide'); // Close the modal after button click
        };
      } else {
        this.createRunWithInstructions(message, editedInstructions);
      }
    } else {
      console.error('Message is empty or thread ID is not set.');
      this.displayAssistantMessage(
        'Failed to send message. Thread ID is not set.'
      );
      this.addToAvailabilityHistory(
        'Failed to send message. Thread ID is not set.'
      );
    }
  }

  createRunWithInstructions(message, instructions = '') {
    const additionalMessages = [
      {
        role: 'user',
        content: instructions || this.serviceDetails,
      },
    ];

    fetch('/service_events/availability_chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
      },
      body: JSON.stringify({
        message: message,
        thread_id: this.threadId,
        additional_messages: additionalMessages,
      }),
    })
      .then(response => response.json())
      .then(data => {
        if (data.response) {
          this.processAssistantResponse(data.response);
          this.removeLoadingIndicator();
        } else if (data.error) {
          console.error('Error from AI:', data.error);
          this.displayAssistantMessage(
            'Error processing your request. Please try again.'
          );
          this.addToAvailabilityHistory(
            'Error processing your request. Please try again.'
          );
        }
      })
      .catch(error => {
        console.error('Error:', error);
        this.displayAssistantMessage(
          'Failed to connect. Please check your connection and try again.'
        );
        this.addToAvailabilityHistory(
          'Failed to connect. Please check your connection and try again.'
        );
      });
  }

  displayAssistantMessage(text) {
    const uniqueId = `assistant-message-${Date.now()}`;
    const assistantMessageElement = document.createElement('div');
    assistantMessageElement.classList.add('chatgpt-message', 'assistant');
    assistantMessageElement.innerHTML = `Availability Assistant: <span id="${uniqueId}"></span>`;
    this.historyTarget.appendChild(assistantMessageElement);

    const assistantMessageSpan = document.getElementById(uniqueId);
    let i = 0;
    const speed = 5;
    const typeWriter = () => {
      if (i < text.length) {
        if (text.charAt(i) === '\n') {
          assistantMessageSpan.innerHTML += '<br>';
        } else {
          assistantMessageSpan.innerHTML += text.charAt(i);
        }
        i++;
        setTimeout(typeWriter, speed);
      } else {
        assistantMessageSpan.innerHTML = text.replace(/\n/g, '<br>');
        this.scrollToBottom();
      }
    };
    typeWriter();
    const dateSelected = text.match(/Date:\s*([\s\S]+?)\s*(?:\n|$)/);
    const timeSlotSelected = text.match(
      /Time:\s*(\d{1,2}:\d{2}\s*(?:AM|PM))\s*to\s*(\d{1,2}:\d{2}\s*(?:AM|PM))/
    );
    if (dateSelected) {
      // Attempt to create a Date object
      const parsedDate = new Date(dateSelected[1]);

      // Check if the date is valid
      const dateValid = !isNaN(parsedDate.getTime());

      if (dateValid) {
        // Show and update the button if the date can be parsed
        this.createServiceEventButtonTarget.style.display = 'block';
        this.createServiceEventButtonTarget.textContent =
          'Confirm Booking for ' + dateSelected[1] + ' ' + timeSlotSelected[1];
      } else {
        this.createServiceEventButtonTarget.style.display = 'none';
        console.log('Invalid date format:', dateSelected[1]);
      }
    } else {
      this.createServiceEventButtonTarget.style.display = 'none';
      console.log('No date found in the text');
    }
  }

  addToAvailabilityHistory(htmlText) {
    this.availabilityHistoryTarget.innerHTML = ''; // Clear existing content
    const availabilityMessageElement = document.createElement('div');
    availabilityMessageElement.innerHTML = htmlText;
    this.availabilityHistoryTarget.appendChild(availabilityMessageElement);
  }

  processAssistantResponse(lastMessage) {
    const htmlSnippetRegex = /```html\n([\s\S]*?)```\n?|'''html\n?([\s\S]*?)(?=\n\s*(?:Service Provider:|$))/;
    const displayedMessages = new Set(
      [...this.historyTarget.querySelectorAll('div')].map(div =>
        div.textContent.trim()
      )
    );
    const availabilityDisplayedMessages = new Set(
      [...this.availabilityHistoryTarget.querySelectorAll('div')].map(div =>
        div.textContent.trim()
      )
    );
    const htmlMatch = lastMessage.match(htmlSnippetRegex);
    const tableMatch = lastMessage.match(/<table[\s\S]*?<\/table>/);
    let htmlSnippet = '';
    if (htmlMatch) {
      htmlSnippet = (htmlMatch[1] || htmlMatch[2] || '').trim();
    } else if (tableMatch) {
      htmlSnippet = tableMatch[0].trim();
    }
    const cleanedMessage = lastMessage
      .replace(htmlSnippetRegex, '')
      .replace(/<table[\s\S]*?<\/table>/, '')
      .trim();

    if (cleanedMessage && !displayedMessages.has(cleanedMessage)) {
      this.displayAssistantMessage(cleanedMessage);
    }
    if (htmlSnippet && !availabilityDisplayedMessages.has(htmlSnippet)) {
      const renderedOptions = this.renderBookingOptions(
        htmlSnippet,
        cleanedMessage
      );
      if (!renderedOptions) {
        this.addToAvailabilityHistory(htmlSnippet);
      }
    }
  }

  parseConfirmationText(text) {
    return {
      serviceProvider: text.match(/Service Provider:\s*([^\n]+)/)?.[1]?.trim(),
      resource: text.match(/Resource:\s*([^\n]+)/)?.[1]?.trim(),
      date: text.match(/Date:\s*([^\n]+)/)?.[1]?.trim(),
      time: text.match(/Time:\s*([^\n]+)/)?.[1]?.trim(),
    };
  }

  parseBookingOptions(htmlSnippet, confirmationText = '') {
    if (!htmlSnippet) return [];

    const confirmation = this.parseConfirmationText(confirmationText);
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlSnippet, 'text/html');
    const table = doc.querySelector('table');
    if (!table) return [];

    const headers = [...table.querySelectorAll('thead th')].map(th =>
      th.textContent.trim().toLowerCase()
    );
    const columnIndex = label =>
      headers.findIndex(header => header.includes(label));
    const dateIndex = columnIndex('date');
    const timeIndex = columnIndex('time');
    const providerIndex = columnIndex('provider');
    const resourceIndex = columnIndex('resource');
    const alternateIndex = headers.findIndex(
      header => header.includes('alternate') || header.includes('other start')
    );

    if (dateIndex < 0 || timeIndex < 0) return [];

    return [...table.querySelectorAll('tbody tr')]
      .map(row => {
        const cells = [...row.querySelectorAll('td')].map(td =>
          td.textContent.trim()
        );
        const time = cells[timeIndex] || '';
        const [startTime, endTime] = time
          .split(/\s+(?:–|-|to)\s+/i)
          .map(value => value.trim());

        return {
          date: cells[dateIndex],
          time,
          startTime,
          endTime,
          serviceProvider: cells[providerIndex] || confirmation.serviceProvider,
          resource: cells[resourceIndex] || confirmation.resource,
          alternateStartTimes: this.parseAlternateStartTimes(
            cells[alternateIndex] || ''
          ),
        };
      })
      .filter(option => option.date && option.startTime && option.endTime);
  }

  renderBookingOptions(htmlSnippet, confirmationText) {
    // Surface only times the model itself returned (headline times plus the
    // alternate starts it validated), grouped by day. No client-side inference.
    const options = this.groupOptionsByDay(
      this.parseBookingOptions(htmlSnippet, confirmationText)
    );
    if (options.length === 0) return false;

    this.availabilityHistoryTarget.innerHTML = '';

    const container = document.createElement('div');
    container.className = 'booking-options booking-options--admin';

    // Surface the appointment length up front — easy to forget when scanning
    // start times.
    const durationLabel = this.slotDurationLabel(options[0]);
    if (durationLabel) {
      const durationNote = document.createElement('div');
      durationNote.className = 'booking-options__duration';
      durationNote.textContent = `${durationLabel} appointment`;
      container.appendChild(durationNote);
    }

    const grid = document.createElement('div');
    grid.className = 'booking-options__grid';

    options.forEach(option => {
      const button = this.buildBookingOptionButton(option);
      button.addEventListener('click', () =>
        this.selectBookingOption(option, button)
      );
      grid.appendChild(button);
    });

    container.appendChild(grid);
    this.availabilityHistoryTarget.appendChild(container);
    this.selectBookingOption(
      options[0],
      grid.querySelector('.booking-options__button')
    );
    return true;
  }

  buildBookingOptionButton(option) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'booking-options__button';

    const time = document.createElement('span');
    time.className = 'booking-options__time';
    time.textContent = `${option.startTime} - ${option.endTime}`;

    const date = document.createElement('span');
    date.className = 'booking-options__date';
    date.textContent = this.formatDateWithDay(option.date);

    const meta = document.createElement('span');
    meta.className = 'booking-options__meta';
    meta.textContent = [option.serviceProvider, option.resource]
      .filter(Boolean)
      .join(' • ');

    button.append(time, date);
    if (meta.textContent) button.appendChild(meta);

    const alternateStarts = this.validAlternateStartTimes(option);
    if (alternateStarts.length > 0) {
      const alternates = document.createElement('span');
      alternates.className = 'booking-options__alternates';

      const label = document.createElement('span');
      label.className = 'booking-options__alternates-label';
      label.textContent = 'Also available';
      alternates.appendChild(label);

      alternateStarts.forEach(startTime => {
        const chip = document.createElement('span');
        chip.className = 'booking-options__alternate-chip';
        chip.textContent = startTime;
        chip.addEventListener('click', event => {
          event.stopPropagation();
          this.selectBookingOption(
            this.optionWithAlternateStart(option, startTime),
            button,
            chip
          );
        });
        alternates.appendChild(chip);
      });

      button.appendChild(alternates);
    }

    return button;
  }

  parseAlternateStartTimes(value) {
    if (!value) return [];

    return value
      .split(/[,;|\n]+/)
      .map(time => time.replace(/^(?:and|or)\s+/i, '').trim())
      .filter(Boolean);
  }

  // Collapse every available slot for a given day (and provider/resource, so the
  // card stays bookable) into a single card: the earliest time becomes the
  // headline and every other available start that day becomes an "Also
  // available" chip.
  groupOptionsByDay(options) {
    const groups = new Map();

    options.forEach(option => {
      const key = [
        option.date,
        option.serviceProvider || '',
        option.resource || '',
      ].join('|');

      if (!groups.has(key)) {
        groups.set(key, { option, starts: [] });
      }

      const group = groups.get(key);
      [option.startTime, ...(option.alternateStartTimes || [])].forEach(time => {
        group.starts.push(time);
      });
    });

    return [...groups.values()].map(({ option, starts }) => {
      const duration =
        this.timeToMinutes(option.endTime) -
        this.timeToMinutes(option.startTime);
      const sortedStarts = this.uniqueTimes(starts).sort(
        (a, b) => this.timeToMinutes(a) - this.timeToMinutes(b)
      );
      const earliest = sortedStarts[0] || option.startTime;
      const earliestMinutes = this.timeToMinutes(earliest);
      const endTime =
        duration > 0 && earliestMinutes !== null
          ? this.minutesToTime(earliestMinutes + duration)
          : option.endTime;

      return {
        date: option.date,
        serviceProvider: option.serviceProvider,
        resource: option.resource,
        startTime: earliest,
        endTime,
        alternateStartTimes: sortedStarts.slice(1),
      };
    });
  }

  uniqueTimes(times) {
    const seen = new Set();

    return times.filter(time => {
      const minutes = this.timeToMinutes(time);
      if (minutes === null || seen.has(minutes)) return false;

      seen.add(minutes);
      return true;
    });
  }

  // Prepend the weekday to a date string for display (e.g. "June 27, 2026" ->
  // "Friday, June 27, 2026"). Display only — the form-population payload keeps
  // the raw date. Leaves the string untouched if it already names a day or
  // can't be parsed.
  formatDateWithDay(dateString) {
    if (!dateString) return dateString;

    const trimmed = String(dateString).trim();
    const firstWord = trimmed.split(/[\s,]+/)[0].toLowerCase();
    const dayPrefixes = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    if (dayPrefixes.some(prefix => firstWord.startsWith(prefix))) {
      return trimmed;
    }

    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) return trimmed;

    const weekday = parsed.toLocaleDateString('en-US', { weekday: 'long' });
    return `${weekday}, ${trimmed}`;
  }

  // Appointment length label (e.g. "60 min") derived from the slot's own
  // start/end so it always matches what is being booked.
  slotDurationLabel(option) {
    if (!option) return '';
    const minutes =
      this.timeToMinutes(option.endTime) - this.timeToMinutes(option.startTime);
    if (!minutes || minutes <= 0) return '';
    return `${minutes} min`;
  }

  validAlternateStartTimes(option) {
    return (option.alternateStartTimes || []).filter(startTime => {
      const startMinutes = this.timeToMinutes(startTime);
      const originalStart = this.timeToMinutes(option.startTime);
      return (
        startMinutes !== null &&
        originalStart !== null &&
        startMinutes > originalStart
      );
    });
  }

  optionWithAlternateStart(option, startTime) {
    const duration =
      this.timeToMinutes(option.endTime) - this.timeToMinutes(option.startTime);
    const alternateStart = this.timeToMinutes(startTime);

    if (!duration || duration <= 0 || alternateStart === null) return option;

    return {
      ...option,
      startTime,
      endTime: this.minutesToTime(alternateStart + duration),
    };
  }

  timeToMinutes(value) {
    const match = String(value)
      .trim()
      .match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
    if (!match) return null;

    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2] || '0', 10);
    const meridian = match[3].toUpperCase();

    if (meridian === 'PM' && hours !== 12) hours += 12;
    if (meridian === 'AM' && hours === 12) hours = 0;

    return hours * 60 + minutes;
  }

  minutesToTime(totalMinutes) {
    const normalized = ((totalMinutes % 1440) + 1440) % 1440;
    const hours24 = Math.floor(normalized / 60);
    const minutes = normalized % 60;
    const meridian = hours24 >= 12 ? 'PM' : 'AM';
    const hours12 = hours24 % 12 || 12;
    const minuteText = String(minutes).padStart(2, '0');

    return `${hours12}:${minuteText} ${meridian}`;
  }

  selectBookingOption(option, selectedButton = null, selectedChip = null) {
    const confirmationText = [
      `Service Provider: ${option.serviceProvider || 'none'}`,
      `Resource: ${option.resource || 'none'}`,
      `Date: ${option.date}`,
      `Time: ${option.startTime} to ${option.endTime}`,
    ].join('\n');

    this.createServiceEventButtonTarget.style.display = 'block';
    // Display the weekday for clarity; the form-population payload above keeps
    // the raw date so the admin date field still parses.
    this.createServiceEventButtonTarget.textContent = `Confirm Booking for ${this.formatDateWithDay(
      option.date
    )} ${option.startTime}`;
    this.createServiceEventButtonTarget.dataset.bookingConfirmation = confirmationText;

    this.availabilityHistoryTarget
      .querySelectorAll('.booking-options__button')
      .forEach(button =>
        button.classList.remove('booking-options__button--selected')
      );
    this.availabilityHistoryTarget
      .querySelectorAll('.booking-options__alternate-chip')
      .forEach(chip =>
        chip.classList.remove('booking-options__alternate-chip--selected')
      );
    if (selectedButton)
      selectedButton.classList.add('booking-options__button--selected');
    if (selectedChip)
      selectedChip.classList.add('booking-options__alternate-chip--selected');
  }

  fetchThreadMessages() {
    fetch(`/service_events/fetch_thread_messages?thread_id=${this.threadId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
      },
    })
      .then(response => response.json())
      .then(data => {
        if (data.messages && data.messages.length > 0) {
          this.processAssistantResponse(data.messages[0]);
        } else {
          console.error('Failed to fetch messages:', data.error);
          this.displayAssistantMessage(
            'Failed to retrieve conversation history.'
          );
          this.addToAvailabilityHistory(
            'Failed to retrieve conversation history.'
          );
        }
        this.removeLoadingIndicator();
      })
      .catch(error => {
        console.error('Error fetching messages:', error);
        this.displayAssistantMessage('Error retrieving messages.');
        this.addToAvailabilityHistory('Error retrieving messages.');
        this.removeLoadingIndicator();
      });
  }

  showLoadingIndicator() {
    if (!document.getElementById('message-text-loading')) {
      const loadingElement = document.createElement('div');
      loadingElement.classList.add('chatgpt-message', 'assistant');
      loadingElement.id = 'message-text-loading';
      loadingElement.innerHTML = `
        <div class="loading-placeholder" style="margin-bottom: 3px;"></div>
      `;
      this.historyTarget.appendChild(loadingElement);
      this.scrollToBottom();
    }
  }

  removeLoadingIndicator() {
    const loadingElement = document.getElementById('message-text-loading');
    if (loadingElement) {
      this.historyTarget.removeChild(loadingElement);
    }
  }

  scrollToBottom() {
    this.historyTarget.scrollTop = this.historyTarget.scrollHeight;
  }

  askNextClassAvailability() {
    this.inputTarget.value = 'What is the next available appointment time?';
    this.send(new Event('click'));
  }

  chooseDate(event) {
    const selectedDate = event.target.value; // This will be in the format 'YYYY-MM-DD'

    //We do the following to avoid having issues with timezones, to make sure the date that is picked by the user is the one we send on the string below
    const [year, month, day] = selectedDate.split('-').map(Number);
    const date = new Date(year, month - 1, day, 12, 0, 0);

    // Format the date to a more natural language format
    const formattedDate = date.toLocaleDateString('en-US', {
      weekday: 'long', // Full name of the day (e.g., 'Tuesday')
      month: 'short', // Short name of the month (e.g., 'Sep')
      day: '2-digit', // Two-digit day (e.g., '03')
      year: 'numeric', // Full numeric year (e.g., '2024')
    });

    this.inputTarget.value = `I would like to book at ${formattedDate}?`;
    this.send(new Event('click'));
  }
}
